import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { t } from '../../i18n/t';
import { useTabs } from '../../tabs';
import type { LopTab } from '../../tabs';
import { isDialogOpen } from '../../tabs/dialogGuard';
import { resolveIcon } from './sidebarIcons';
import { LopGridContextMenu, type LopMenuItem } from '../LopGridContextMenu';
import './LopTabBar.css';

interface OpenMenu {
  x: number;
  y: number;
  tab: LopTab;
}

// Confirms closing every DIRTY tab in `list` (window.confirm, one prompt per
// tab — plan §3.6). Aborts the whole batch (returns false) the moment the
// user cancels any single prompt, so "close others"/"close all" never close
// some dirty tabs and leave others open based on prompt order.
function confirmCloseDirtyTabs(list: LopTab[]): boolean {
  for (const tab of list) {
    if (!window.confirm(`${tab.title}: ${t('tabs.unsavedConfirm')}`)) return false;
  }
  return true;
}

/**
 * RTL scrollable strip under the TopBar, one entry per open internal tab
 * (plan §3.4). Activating a tab navigates via `{replace:true,
 * state:{lopTabActivate:true}}` — TabRouterSync recognizes that flag and
 * treats it as pure focus (never the generic REPLACE "collapse tabs" rule).
 */
export function LopTabBar() {
  const { tabs, activeKey, close, closeOthers, closeAll } = useTabs();

  // Closing a dirty tab (×, middle-click, "close others", "close all") asks
  // window.confirm first (plan §3.6) — the pinned home tab is never dirty, so
  // it never appears in these lists. Every user-driven tab-bar action below
  // (activate/close/closeOthers/closeAll/open-context-menu) ALSO no-ops
  // while a modal dialog is open anywhere in the tree (plan §4 corner,
  // dialogGuard.ts) — same "the bar ignores" policy the Alt+Q switcher
  // already had. Simplest safe behavior: rather than closing the dialog or
  // leaving it open-but-unreachable in a backgrounded pane, the tab bar just
  // doesn't act until the dialog itself is closed.
  const closeWithConfirm = useCallback(
    (tab: LopTab) => {
      if (isDialogOpen()) return;
      if (tab.dirty && !confirmCloseDirtyTabs([tab])) return;
      close(tab.key);
    },
    [close],
  );
  const closeOthersWithConfirm = useCallback(
    (tab: LopTab) => {
      if (isDialogOpen()) return;
      const dirty = tabs.filter((t) => t.key !== tab.key && t.dirty);
      if (dirty.length > 0 && !confirmCloseDirtyTabs(dirty)) return;
      closeOthers(tab.key);
    },
    [tabs, closeOthers],
  );
  const closeAllWithConfirm = useCallback(() => {
    if (isDialogOpen()) return;
    const dirty = tabs.filter((t) => t.dirty);
    if (dirty.length > 0 && !confirmCloseDirtyTabs(dirty)) return;
    closeAll();
  }, [tabs, closeAll]);
  const navigate = useNavigate();
  const [menu, setMenu] = useState<OpenMenu | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);

  // Keep the active tab scrolled into view (new tabs open after it, so it
  // can end up off the visible strip).
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeKey]);

  const activate = useCallback(
    (tab: LopTab) => {
      if (isDialogOpen()) return;
      navigate(tab.location, { replace: true, state: { lopTabActivate: true } });
    },
    [navigate],
  );

  const handleAuxClick = useCallback(
    (e: React.MouseEvent, tab: LopTab) => {
      if (e.button !== 1 || tab.pinned) return; // middle-click
      e.preventDefault();
      closeWithConfirm(tab);
    },
    [closeWithConfirm],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent, tab: LopTab) => {
    e.preventDefault();
    if (isDialogOpen()) return;
    setMenu({ x: e.clientX, y: e.clientY, tab });
  }, []);

  const menuItems: LopMenuItem[] = menu
    ? [
        { label: t('tabs.close'), onClick: () => closeWithConfirm(menu.tab), disabled: menu.tab.pinned },
        { label: t('tabs.closeOthers'), onClick: () => closeOthersWithConfirm(menu.tab) },
        { label: t('tabs.closeAll'), onClick: () => closeAllWithConfirm(), dividerBefore: true },
      ]
    : [];

  return (
    <div className="lop-tab-bar-wrap">
      <div className="lop-tab-bar" role="tablist" aria-label={t('tabs.ariaLabel')} ref={scrollerRef}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          const Icon = resolveIcon(tab.iconName);
          return (
            <div
              key={tab.key}
              ref={isActive ? activeTabRef : undefined}
              role="tab"
              aria-selected={isActive}
              className={'lop-tab-bar__tab' + (isActive ? ' lop-tab-bar__tab--active' : '')}
              onClick={() => activate(tab)}
              onAuxClick={(e) => handleAuxClick(e, tab)}
              onContextMenu={(e) => handleContextMenu(e, tab)}
              title={tab.title}
            >
              {Icon && <Icon size={14} className="lop-tab-bar__icon" aria-hidden="true" />}
              <span className="lop-tab-bar__title">{tab.title}</span>
              {/* Dirty dot: visible by default, hidden on hover (CSS) — the ×
                  below takes its place once hovered (plan §3.4/§3.6). */}
              {tab.dirty && <span className="lop-tab-bar__dirty-dot" aria-hidden="true" />}
              {!tab.pinned && (
                <button
                  type="button"
                  className="lop-tab-bar__close"
                  aria-label={t('tabs.close')}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeWithConfirm(tab);
                  }}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {menu && (
        <LopGridContextMenu x={menu.x} y={menu.y} items={menuItems} onClose={() => setMenu(null)} />
      )}
    </div>
  );
}
